import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { allArticles, hotArticles, siteInfo } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground-950 mb-4">文章不存在</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = allArticles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const mockComments = [
    {
      id: 1,
      author: '吃瓜不嫌事大',
      avatar: 'https://readdy.ai/api/search-image?query=Simple%20minimalist%20user%20avatar%20icon%20on%20clean%20white%20background%2C%20flat%20design%2C%20warm%20colors&width=80&height=80&seq=cmt-avatar-01&orientation=squarish',
      content: '天哪这个瓜太大了！我之前就听说了一些传闻，没想到真相远比想象中劲爆。希望后续能有更多实锤出来，坐等吃瓜！',
      likes: 3205,
      date: '2026-07-07',
    },
    {
      id: 2,
      author: '娱乐圈观察员',
      avatar: 'https://readdy.ai/api/search-image?query=Simple%20minimalist%20observer%20avatar%20icon%20on%20clean%20white%20background%2C%20flat%20design&width=80&height=80&seq=cmt-avatar-02&orientation=squarish',
      content: '作为圈内人，我只能说文章说的只是冰山一角。这个圈子水太深了，很多事情外面根本想象不到。小编再多挖挖，还有更劲爆的。',
      likes: 2810,
      date: '2026-07-06',
    },
    {
      id: 3,
      author: '理性吃瓜群众',
      avatar: 'https://readdy.ai/api/search-image?query=Simple%20minimalist%20watermelon%20icon%20avatar%20on%20clean%20white%20background%2C%20flat%20design&width=80&height=80&seq=cmt-avatar-03&orientation=squarish',
      content: '建议大家理性看待，现在还没有官方回应，不要过度解读。不过说实话，这个证据链确实很完整，大概率是真的。',
      likes: 1567,
      date: '2026-07-07',
    },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.heiliaochigua.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `https://www.heiliaochigua.com/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    keywords: article.tags.join(', '),
    articleSection: article.category,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: 'https://www.heiliaochigua.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.heiliaochigua.com/article/${article.slug}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${article.title}是真的吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `本文基于多方知情人士爆料和现场证据整理。截至${article.publishDate}，相关方尚未给出官方回应，请读者理性看待、持续关注后续进展。`,
        },
      },
      {
        '@type': 'Question',
        name: '黑料吃瓜网的信息来源可靠吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黑料吃瓜网每一条爆料均经过编辑团队的多方交叉验证，确保信息真实可靠。我们拒绝虚假谣言和恶意中伤，致力于为吃瓜群众提供最真实的娱乐资讯。',
        },
      },
      {
        '@type': 'Question',
        name: '如何获取更多类似爆料？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以访问黑料吃瓜网的${article.category}频道，或通过首页热榜查看全网实时最热的吃瓜爆料。我们每日更新，网曝一区二区猛料不打烊。`,
        },
      },
      {
        '@type': 'Question',
        name: '黑料吃瓜网如何保护爆料人隐私？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们采用严格的匿名投稿机制与信息加密技术，全方位保护爆料人隐私安全。所有投稿均经过脱敏处理，确保知情人敢于说出真相。',
        },
      },
      {
        '@type': 'Question',
        name: '如果发现不实信息怎么办？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '如果您发现文章内容有不实之处，欢迎发送邮件至 feedback@heiliaochigua.com 进行反馈。我们将在收到反馈后24小时内进行核实，确认不实将第一时间更新或撤稿。',
        },
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <nav className="pt-20 pb-4 px-4 md:px-6 max-w-4xl mx-auto" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line text-xs"></i>
            </li>
            <li>
              <Link
                to={`/category/${article.categorySlug}`}
                className="hover:text-primary-600 transition-colors"
              >
                {article.category}
              </Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line text-xs"></i>
            </li>
            <li className="text-foreground-900 font-medium truncate max-w-[200px]">
              {article.title}
            </li>
          </ol>
        </nav>

        <article>
          <header className="px-4 md:px-6 max-w-4xl mx-auto pb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black font-heading text-foreground-950 leading-tight mb-6">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={article.avatar}
                  alt={article.author}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-semibold text-foreground-900">{article.author}</p>
                  <p className="text-xs text-foreground-500">
                    {article.publishDate} · 阅读 {article.readCount}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </header>

          <div className="px-4 md:px-6 max-w-4xl mx-auto pb-8">
            <div className="rounded-xl overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover object-top"
              />
            </div>

            <div className="prose prose-lg max-w-none text-foreground-800 leading-relaxed">
              <p className="text-lg mb-6">{article.excerpt}</p>

              <h2 className="text-2xl font-bold font-heading text-foreground-950 mt-10 mb-4">
                事件详情
              </h2>
              <p className="mb-4">
                据多位知情人士向黑料吃瓜网独家透露，此次事件在圈内早有传闻，但一直缺乏实质性证据。
                直到近日，一位不愿透露姓名的内部人士向本站提供了大量一手资料，包括聊天记录截图、
                现场照片以及相关合同文件，使得整个事件的真相逐渐浮出水面。
              </p>
              <p className="mb-4">
                从目前掌握的信息来看，该事件涉及多方利益纠葛，背后牵扯到的不仅有同行竞争，
                还有资本力量的介入。一位资深娱乐记者分析称："这种情况在圈内并不罕见，
                只不过大多数时候都被成功掩盖了。这次之所以会曝光，主要是因为内部出现了利益分歧。"
              </p>

              <h2 className="text-2xl font-bold font-heading text-foreground-950 mt-10 mb-4">
                各方反应
              </h2>
              <p className="mb-4">
                事件曝光后，相关方迅速做出了反应。当事人团队在第一时间通过社交媒体发布了声明，
                称"相关内容纯属捏造，将通过法律途径维护合法权益"。然而，网友们并不买账，
                纷纷在评论区指出声明中存在的多处漏洞。
              </p>
              <p className="mb-4">
                与此同时，多位圈内人士也通过小号或匿名方式表达了对事件的看法。
                有人表示"早就知道会有这么一天"，也有人呼吁"让子弹再飞一会儿，真相总会水落石出"。
              </p>

              <h3 className="text-xl font-bold font-heading text-foreground-900 mt-8 mb-3">
                深度分析
              </h3>
              <p className="mb-4">
                从行业角度来看，此次事件折射出的是整个娱乐圈深层次的生态问题。
                长期以来，信息不对称、权力不对等、利益分配不均等问题一直存在，
                只是被光鲜亮丽的外表所掩盖。这次的爆料就像一面镜子，照出了许多被刻意回避的现实。
              </p>

              <h4 className="text-lg font-bold font-heading text-foreground-800 mt-6 mb-2">
                后续走向预测
              </h4>
              <p className="mb-4">
                据业内人士分析，此次事件可能还会持续发酵一段时间。如果涉及的当事人无法提供
                有力的反驳证据，其商业价值和公众形象都将受到严重冲击。目前已有多个品牌方
                表示将密切关注事态发展，不排除暂缓合作的可能性。
              </p>

              <p className="mt-8 text-sm text-foreground-500 italic">
                免责声明：本文内容基于公开信息和知情人士爆料整理，仅供参考。
                黑料吃瓜网不对本文内容的真实性、准确性和完整性做任何保证。
                如有不实之处，欢迎相关方提供证据进行澄清。
              </p>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="py-10 md:py-14 bg-background-100">
              <div className="px-4 md:px-6 max-w-7xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-black font-heading text-foreground-950 mb-8">
                  相关爆料
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard
                      key={ra.id}
                      slug={ra.slug}
                      title={ra.title}
                      excerpt={ra.excerpt}
                      category={ra.category}
                      categorySlug={ra.categorySlug}
                      imageUrl={ra.imageUrl}
                      author={ra.author}
                      avatar={ra.avatar}
                      publishDate={ra.publishDate}
                      readCount={ra.readCount}
                      isHot={ra.isHot}
                      isExclusive={ra.isExclusive}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="py-10 md:py-14 bg-background-50">
            <div className="px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="text-2xl font-serif font-bold text-foreground-950 text-center mb-8">
                【 网友热评 】
              </h2>
              <div className="space-y-6">
                {mockComments.map((comment) => (
                  <div key={comment.id} className="bg-background-100 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-foreground-900 text-background-50 text-xs font-semibold rounded-full">
                        <i className="ri-star-fill text-accent-400 text-xs"></i>
                        {comment.likes}
                      </span>
                    </div>
                    <p className="text-foreground-800 leading-relaxed mb-3 text-sm">
                      &ldquo;{comment.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <img
                        src={comment.avatar}
                        alt={comment.author}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">
                          {comment.author}
                        </p>
                        <p className="text-xs text-foreground-500">{comment.date}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}